import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Button, Checkbox, message, Select, Row, Col } from "antd";
import { EnvironmentOutlined } from "@ant-design/icons";
import { jwtDecode } from "jwt-decode";
import DeliveryAddressServ from "../service/DeliveryAddressesServ";
import GlobalMap from "../util/GlobalMap";
import "../css/ChangeLocationContent.css";

const { Option } = Select;

const DeliveryAddress = ({
    open,
    onClose,
    addressDetails,
    isEditing,
    refreshAddress,
    overrideUser // Optional: Object containing { id, name, email, phone } to bypass JWT decode
}) => {
    const [form] = Form.useForm();
    const [address, setAddress] = useState("");
    const [customerId, setCustomerId] = useState("");

    useEffect(() => {
        if (!open) return;

        // If an override user is provided (e.g. from Admin panel), use it directly
        if (overrideUser) {
            setCustomerId(overrideUser.id);
            form.setFieldValue("customerId", overrideUser.id);
            form.setFieldValue("name", overrideUser.name);
            form.setFieldValue("email", overrideUser.email);
            if (overrideUser.phone) {
                form.setFieldValue("phoneNumber", overrideUser.phone);
            }
            return;
        }

        const token = localStorage.getItem("token") || localStorage.getItem("customerToken");
        if (token) {
            try {
                const decoded = jwtDecode(token);
                const name = decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];
                const id = decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"];
                const email = decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"];

                setCustomerId(id);
                form.setFieldValue("customerId", id);
                form.setFieldValue("name", name);
                form.setFieldValue("email", email);

                import("../service/AuthServ").then(AuthServ => {
                    AuthServ.default.single().then(res => {
                        if (res?.data?.phone) {
                            form.setFieldValue("phoneNumber", res.data.phone);
                        }
                    }).catch(err => console.error("Failed to fetch user profile", err));
                });
            } catch (e) {
                console.error("Decode failed");
            }
        }
    }, [open, overrideUser, form]);

    /* ------- Prefill when editing ------- */
    useEffect(() => {
        if (open) {
            if (isEditing && addressDetails) {
                const fetchPhoneIfNeeded = async () => {
                    if (!addressDetails.phoneNumber && !overrideUser?.phone) {
                        try {
                            const AuthServ = (await import("../service/AuthServ")).default;
                            const res = await AuthServ.single();
                            if (res?.data?.phone) return res.data.phone;
                        } catch (err) {
                            console.error("Failed to fetch phone", err);
                        }
                    }
                    return addressDetails.phoneNumber || overrideUser?.phone;
                };

                fetchPhoneIfNeeded().then(phoneNumber => {
                    const normalizedData = {
                        ...addressDetails,
                        pincode: addressDetails.pincode || addressDetails.zipCode || "",
                        phoneNumber: phoneNumber || ""
                    };
                    form.setFieldsValue(normalizedData);
                    setAddress(addressDetails.street || addressDetails.address || "");
                });
            } else {
                form.resetFields();
                setAddress("");
            }
        }
    }, [open, isEditing, addressDetails, form]);

    /* ------- Auto-Fill Fields ------- */
    const fillAddressFields = (data) => {
        form.setFieldsValue({
            street: data.address || "",
            city: data.city || "",
            district: data.district || "",
            state: data.state || "",
            pincode: data.zipCode || "",
            country: data.country || "",
            latitude: data.latitude,
            longitude: data.longitude,
        });
        setAddress(data.address || "");
    };

    // Geocode manually typed address on blur
    const handleManualAddressBlur = () => {
        const street = form.getFieldValue("street");
        const city = form.getFieldValue("city");
        const district = form.getFieldValue("district");
        const pincode = form.getFieldValue("pincode");

        const fullAddr = [street, city, district, pincode].filter(Boolean).join(", ");

        if (fullAddr && window.google) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address: fullAddr }, (results, status) => {
                if (status === "OK" && results[0]) {
                    const result = results[0];
                    const loc = result.geometry.location;
                    const comps = result.address_components || [];
                    const getComp = (t) => comps.find(c => c.types.includes(t))?.long_name || "";

                    let zipCode = getComp("postal_code") || getComp("zip_code");
                    if (!zipCode) {
                        for (const comp of comps) {
                            if (/\b\d{6}\b/.test(comp.long_name)) {
                                zipCode = comp.long_name.match(/\b\d{6}\b/)[0];
                                break;
                            }
                        }
                    }
                    if (!zipCode && result.formatted_address) {
                        const match = result.formatted_address.match(/\b\d{6}\b/);
                        if (match) zipCode = match[0];
                    }

                    let districtFound = getComp("administrative_area_level_2");
                    if (!districtFound) {
                        const distComp = comps.find(c =>
                            c.long_name.toLowerCase().includes("district") ||
                            c.types.includes("administrative_area_level_3")
                        );
                        districtFound = distComp ? distComp.long_name.replace(/ district/i, "") : "";
                    }
                    if (!districtFound) districtFound = getComp("locality");

                    form.setFieldsValue({
                        latitude: loc.lat(),
                        longitude: loc.lng(),
                        city: form.getFieldValue("city") || getComp("locality") || getComp("sublocality"),
                        district: form.getFieldValue("district") || districtFound,
                        state: form.getFieldValue("state") || getComp("administrative_area_level_1"),
                        pincode: form.getFieldValue("pincode") || zipCode,
                    });
                    setAddress(result.formatted_address);
                }
            });
        }
    };

    /* ------- Submit Form ------- */
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const tenantId = localStorage.getItem("tenantId");
            const validTenantId = tenantId && tenantId.length === 36 ? tenantId : "3fa85f64-5717-4562-b3fc-2c963f66afa6";

            const payload = {
                ...values,
                tenantId: validTenantId,
                customerId: customerId,
                latitude: Number(values.latitude) || 0,
                longitude: Number(values.longitude) || 0,
            };

            if (isEditing) {
                await DeliveryAddressServ.update(addressDetails._id || addressDetails.id, payload);
                message.success("Address updated successfully!");
            } else {
                await DeliveryAddressServ.create(payload);
                message.success("Address added successfully!");
            }

            handleClose(true);
        } catch (error) {
            message.error("Failed to save address");
            console.error(error);
        }
    };

    const handleClose = (shouldRefresh) => {
        refreshAddress(shouldRefresh);
        form.resetFields();
        if (typeof onClose === 'function') onClose();
    };

    return (
        <Modal
            title={
                <span style={{ fontSize: '14px', fontWeight: 600 }} className="pcu-color-purple">
                    {isEditing ? "Edit Address" : "Add New Address"}
                </span>
            }
            open={open}
            onCancel={() => handleClose(false)}
            width={720}
            footer={null}
            centered
            className="compact-address-modal"
        >
            <style>{`
                .compact-address-modal .ant-modal-close {
                    top: 8px !important;
                    right: 8px !important;
                }
                .compact-address-modal .ant-modal-header {
                    margin-bottom: 8px !important;
                    padding: 8px 0 4px 0 !important;
                }
                .compact-address-modal .ant-modal-content {
                    padding: 12px 16px !important;
                }
                .compact-address-modal .ant-form-item-label > label {
                    font-size: 11px !important;
                    font-weight: 500 !important;
                    height: auto !important;
                    line-height: 1.2 !important;
                }
                .compact-address-modal .ant-form-item {
                    margin-bottom: 8px !important;
                }
                .compact-address-modal .ant-input,
                .compact-address-modal .ant-select-selector {
                    font-size: 12px !important;
                    height: 28px !important;
                    line-height: 28px !important;
                }
                .compact-address-modal .ant-select-single .ant-select-selector .ant-select-selection-item {
                    line-height: 26px !important;
                }
                .compact-address-modal .pcu-flex-end {
                    margin-top: 12px !important;
                }
                .compact-address-modal .ant-btn {
                    font-size: 12px !important;
                    height: 28px !important;
                    padding: 0 16px !important;
                    border-radius: 6px !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    font-weight: 500 !important;
                    box-shadow: none !important;
                }
                .compact-address-modal .ant-btn-primary {
                    background-color: #f97316 !important;
                    border-color: #f97316 !important;
                    color: #fff !important;
                }
                .compact-address-modal .ant-btn-default {
                    border: 1px solid #d9d9d9 !important;
                    background-color: #fff !important;
                    color: rgba(0, 0, 0, 0.88) !important;
                }
            `}</style>
            <Form form={form} layout="vertical" size="small" onFinish={handleSubmit}>
                <Row gutter={12} align="top">
                    <Col xs={24} md={12}>
                        <GlobalMap
                            height="290px"
                            address={address}
                            onLocationSelect={fillAddressFields}
                            markerPosition={
                                form.getFieldValue("latitude") && form.getFieldValue("longitude")
                                    ? { lat: parseFloat(form.getFieldValue("latitude")), lng: parseFloat(form.getFieldValue("longitude")) }
                                    : null
                            }
                        />
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="addressType"
                            label={<span className="pcu-color-purple">Address Type</span>}
                            rules={[{ required: true, message: 'Please select address type' }]}
                            initialValue="home"
                        >
                            <Select
                                placeholder="Select address type"
                                onChange={(val) => {
                                    // Force re-render for conditional field
                                    form.setFieldsValue({ addressType: val });
                                }}
                            >
                                <Option value="home">Home</Option>
                                <Option value="office">Office / Work</Option>
                                <Option value="pickup">Pickup Point</Option>
                                <Option value="billing">Billing Address</Option>
                                <Option value="other">Other</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) => prevValues.addressType !== currentValues.addressType}
                        >
                            {({ getFieldValue }) =>
                                getFieldValue('addressType') === 'other' ? (
                                    <Form.Item
                                        name="otherAddressType"
                                        label={<span className="pcu-color-purple">Other Address Label</span>}
                                        rules={[{ required: true, message: 'Please specify (e.g. Friends House)' }]}
                                    >
                                        <Input placeholder="Enter label (e.g. School, Gym)" />
                                    </Form.Item>
                                ) : null
                            }
                        </Form.Item>

                        <Form.Item
                            name="street"
                            label={<span className="pcu-color-purple">Street</span>}
                            rules={[{ required: true, message: 'Please enter street address' }]}
                        >
                            <Input placeholder="Enter street address" onBlur={handleManualAddressBlur} />
                        </Form.Item>

                        <Row gutter={8}>
                            <Col span={12}>
                                <Form.Item
                                    name="city"
                                    label={<span className="pcu-color-purple">City/Taluk</span>}
                                    rules={[{ required: true, message: 'Please enter city' }]}
                                >
                                    <Input placeholder="Enter city" onBlur={handleManualAddressBlur} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="district"
                                    label={<span className="pcu-color-purple">District</span>}
                                >
                                    <Input placeholder="Enter district" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            name="pincode"
                            label={<span className="pcu-color-purple">Pincode</span>}
                            rules={[
                                { required: true, message: 'Please enter pincode' },
                                { len: 6, message: 'Pincode must be 6 digits' },
                                { pattern: /^[0-9]{6}$/, message: 'Pincode must be 6 digits' }
                            ]}
                        >
                            <Input placeholder="Enter pincode" maxLength={6} onBlur={handleManualAddressBlur} />
                        </Form.Item>

                        <Row gutter={8}>
                            <Col span={12}>
                                <Form.Item
                                    name="latitude"
                                    label={<span className="pcu-color-purple">Latitude</span>}
                                >
                                    <Input readOnly style={{ background: '#fafafa', color: '#595959' }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="longitude"
                                    label={<span className="pcu-color-purple">Longitude</span>}
                                >
                                    <Input readOnly style={{ background: '#fafafa', color: '#595959' }} />
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* Hidden fields for required data */}
                        <div style={{ display: 'none' }}>
                            <Form.Item name="name"><Input /></Form.Item>
                            <Form.Item name="phoneNumber"><Input /></Form.Item>
                            <Form.Item name="state" initialValue="Tamil Nadu"><Input /></Form.Item>
                            <Form.Item name="country" initialValue="India"><Input /></Form.Item>
                            <Form.Item name="customerId"><Input /></Form.Item>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                            <Button
                                onClick={() => handleClose(false)}
                                type="default"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                            >
                                OK
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default DeliveryAddress;
